import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/**
 * Available icon names. Add new entries to {@link ICON_PATHS} below.
 */
export type IconName =
  | 'building'
  | 'document'
  | 'chart'
  | 'arrow-right'
  | 'sparkles'
  | 'briefcase'
  | 'calendar'
  | 'users'
  | 'shield'
  | 'tag'
  | 'trash'
  | 'restore'
  | 'plus'
  | 'upload'
  | 'check'
  | 'search'
  | 'filter'
  | 'x'
  | 'x-circle'
  | 'info'
  | 'alert-triangle'
  | 'alert-octagon'
  | 'chevron-up'
  | 'chevron-down'
  | 'chevron-left'
  | 'chevron-right'
  | 'arrow-left'
  | 'book'
  | 'file-text'
  | 'database'
  | 'chevrons-up-down'
  | 'pencil'
  | 'eye'
  | 'download'
  | 'clock'
  | 'more-vertical'
  | 'refresh'
  | 'wallet'
  | 'inbox'
  | 'zap'
  | 'trending-up'
  | 'sun'
  | 'moon'
  | 'menu'
  | 'log-in'
  | 'bug'
  | 'message-square'
  | 'grip'
  | 'lightbulb'
  | 'camera'
  | 'image'
  | 'phone'
  | 'scale'
  | 'copy'
  | 'user'
  | 'log-out'
  | 'smartphone'
  | 'lock'
  | 'check-circle'
  | 'mail'
  | 'bot';

/**
 * Inline SVG path data for each icon (24×24 viewBox, stroke-based).
 *
 * Icons are stroke-only and inherit `currentColor`, so they theme automatically
 * with the surrounding text colour. This avoids a webfont/network dependency and
 * keeps the bundle self-contained. Sourced from the Lucide icon set (ISC licence).
 */
const ICON_PATHS: Record<IconName, string> = {
  // Storefront / register a business
  building: '<path d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-4M9 9v.01M9 12v.01M9 15v.01M9 18v.01"/>',
  // Document with lines — initiate an application
  document:
    '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M9 13h6M9 17h6"/>',
  // Trending chart — get your loan results
  chart: '<path d="M3 3v18h18"/><path d="m7 14 4-4 3 3 5-6"/>',
  'arrow-right': '<path d="M5 12h14M13 6l6 6-6 6"/>',
  sparkles:
    '<path d="M12 3v4M12 17v4M3 12h4M17 12h4M6.3 6.3l2.8 2.8M14.9 14.9l2.8 2.8M17.7 6.3l-2.8 2.8M9.1 14.9l-2.8 2.8"/>',
  // Briefcase — industry / sector
  briefcase:
    '<rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>',
  // Calendar — trading since
  calendar: '<rect x="3" y="4" width="18" height="18" rx="2"/><path d="M3 10h18M8 2v4M16 2v4"/>',
  // People — agent context
  users:
    '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>',
  // Shield — system / platform context
  shield: '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>',
  // Tag — entity type / legal form
  tag: '<path d="M3 7v5l9 9 7-7-9-9H4a1 1 0 0 0-1 1z"/><path d="M7.5 7.5v.01"/>',
  // Trash — delete a business
  trash:
    '<path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M10 11v6M14 11v6"/>',
  // Counter-clockwise arrow — restore a deleted business
  restore: '<path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5"/>',
  // Plus — add a business
  plus: '<path d="M12 5v14M5 12h14"/>',
  // Upload — attach a document
  upload: '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"/>',
  // Check — accept / confirm
  check: '<path d="M20 6 9 17l-5-5"/>',
  // Check inside a circle — a completed / settled positive state (quorum met)
  'check-circle': '<path d="M21.801 10A10 10 0 1 1 17 3.335"/><path d="m9 11 3 3L22 4"/>',
  // Overlapping pages — copy a value to the clipboard
  copy: '<rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>',
  // Magnifying glass — search inputs
  search: '<circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>',
  // Funnel — filter controls
  filter: '<path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/>',
  // Cross — clear / dismiss
  x: '<path d="M18 6 6 18M6 6l12 12"/>',
  // Cross in circle — error toast / destructive feedback
  'x-circle': '<circle cx="12" cy="12" r="10"/><path d="m15 9-6 6M9 9l6 6"/>',
  'alert-octagon':
    '<path d="M7.86 2h8.28L22 7.86v8.28L16.14 22H7.86L2 16.14V7.86z"/><path d="M12 8v4M12 16h.01"/>',
  // Info in circle — informational banners / toasts
  info: '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/>',
  // Triangle — warning state
  'alert-triangle':
    '<path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><path d="M12 9v4M12 17h.01"/>',
  // Chevrons — sort indicators, pagination, breadcrumbs, accordions
  'chevron-up': '<path d="m18 15-6-6-6 6"/>',
  'chevron-down': '<path d="m6 9 6 6 6-6"/>',
  'chevron-left': '<path d="m15 18-6-6 6-6"/>',
  'chevron-right': '<path d="m9 18 6-6-6-6"/>',
  // Arrow pointing left — back navigation
  'arrow-left': '<path d="M19 12H5M12 5l-7 7 7 7"/>',
  // Book — documentation / handbook
  book: '<path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>',
  // File with text lines — log / document viewer
  'file-text':
    '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M9 13h6M9 17h4"/>',
  // Cylinder stack — database / data store
  database:
    '<ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5"/><path d="M3 12c0 1.66 4.03 3 9 3s9-1.34 9-3"/>',
  // Stacked chevrons — unsorted column affordance
  'chevrons-up-down': '<path d="m7 15 5 5 5-5M7 9l5-5 5 5"/>',
  // Pencil — edit row action
  pencil: '<path d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/>',
  // Eye — view row action
  eye: '<path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/>',
  // Download — export / download offer
  download: '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/>',
  // Envelope — a step that emails someone (journey flowchart)
  mail: '<rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>',
  // Clock — pending / expiry / timeline
  clock: '<circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>',
  // Vertical ellipsis — row action menu trigger
  'more-vertical':
    '<circle cx="12" cy="5" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="12" cy="19" r="1"/>',
  // Clockwise arrow — re-run / refresh action
  refresh: '<path d="M21 2v6h-6"/><path d="M21 13a9 9 0 1 1-3-7.7L21 8"/>',
  // Wallet — funding facilitated / money
  wallet:
    '<path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/><path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/><path d="M18 12a2 2 0 0 0 0 4h4v-4z"/>',
  // Inbox — a review queue
  inbox:
    '<path d="M22 12h-6l-2 3h-4l-2-3H2"/><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>',
  // Lightning — live / active / fast
  zap: '<path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z"/>',
  // Trending up — portfolio growth / intake
  'trending-up': '<path d="M22 7 13.5 15.5l-5-5L2 17"/><path d="M16 7h6v6"/>',
  // Sun — light mode toggle
  sun: '<circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/>',
  // Moon — dark mode toggle
  moon: '<path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>',
  // Menu / hamburger — mobile nav open
  menu: '<path d="M3 12h18M3 6h18M3 18h18"/>',
  // Log in — unauthenticated state login button
  'log-in': '<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>',
  // Bug — debug menu toggle (local dev only)
  bug: '<path d="m8 2 1.88 1.88M14.12 3.88 16 2M9 7.13v-1a3.003 3.003 0 1 1 6 0v1M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6M3 14h3M18 14h3M3 9h2M19 9h2M12 20v-9"/>',
  // Speech bubble — feedback / reports
  'message-square': '<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>',
  // Nine dots in a square — the Report a suggestion/bug launcher
  grip: '<circle cx="12" cy="5" r="1"/><circle cx="19" cy="5" r="1"/><circle cx="5" cy="5" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/><circle cx="12" cy="19" r="1"/><circle cx="19" cy="19" r="1"/><circle cx="5" cy="19" r="1"/>',
  // Lightbulb — a suggestion / idea
  lightbulb:
    '<path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"/><path d="M9 18h6M10 22h4"/>',
  // Camera — take a screenshot
  camera:
    '<path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/>',
  // Picture — an attached screenshot
  image:
    '<rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.1-3.1a2 2 0 0 0-2.8 0L6 21"/>',
  // Phone — contact / tel link
  phone:
    '<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>',
  // Balance scale — Credit Committee resolution / collective decision
  scale: '<path d="M12 3v18M7 21h10M3 7h18M3 7l3 7H0l3-7zM21 7l3 7h-6l3-7zM6 3h12"/>',
  // Person — account / profile menu
  user: '<path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>',
  // Door with arrow — sign out
  'log-out':
    '<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><path d="m16 17 5-5-5-5M21 12H9"/>',
  // Phone — authenticator app / 2FA device
  smartphone: '<rect width="14" height="20" x="5" y="2" rx="2" ry="2"/><path d="M12 18h.01"/>',
  // Robot — AI-generated content (see app-ai-mark)
  bot: '<path d="M12 8V4H8"/><rect width="16" height="12" x="4" y="8" rx="2"/><path d="M2 14h2M20 14h2M15 13v2M9 13v2"/>',
  // Padlock — security / 2FA section
  lock: '<rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>',
};

/**
 * Renders an inline SVG icon by name.
 *
 * Usage: `<app-icon name="building" />` or `<app-icon name="chart" [size]="32" />`.
 * Colour is inherited from the parent via `currentColor` — set `color` on the host.
 */
@Component({
  selector: 'app-icon',
  template: `<span
    class="app-icon"
    [style.width.px]="size()"
    [style.height.px]="size()"
    [innerHTML]="svg()"
  ></span>`,
  styles: [
    `
      .app-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      .app-icon ::ng-deep svg {
        width: 100%;
        height: 100%;
        display: block;
      }
    `,
  ],
  host: {
    // The host tag (<app-icon>) is otherwise an unstyled inline element, so
    // whitespace/newlines around it in a parent template render as a real
    // trailing space (e.g. inside flex/centered containers like icon badges).
    // display: inline-flex keeps icons inline with text while removing that
    // whitespace-node side effect.
    style: 'display: inline-flex;',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IconComponent {
  private readonly sanitizer = inject(DomSanitizer);

  readonly name = input.required<IconName>();
  readonly size = input(24);

  protected readonly svg = computed<SafeHtml>(() => {
    const paths = ICON_PATHS[this.name()] ?? '';
    const markup =
      `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" ` +
      `stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">${paths}</svg>`;
    // Markup is built entirely from our own constants — no user input is interpolated.
    return this.sanitizer.bypassSecurityTrustHtml(markup);
  });
}
